<template>
  <v-app-bar
    :color="$vuetify.theme.dark ? 'grey darken-4' : 'white'"
    class="pr-9"
    app
    dense
    elevation="1"
  >
    <v-app-bar-nav-icon
      v-if="$vuetify.breakpoint.mdAndDown"
      @click="$emit('drawerOpen')"
    />

    <v-chip
      v-if="activeRequestId"
      :color="$vuetify.theme.dark ? 'primary lighten-1' : 'primary'"
      text-color="white"
      class="ml-4"
      close
      @click:close="clearActiveRequest"
    >
      <v-icon left small>mdi-checkbox-marked-circle</v-icon>
      Τώρα δουλεύετε με την αίτηση {{ activeRequestId }}
    </v-chip>

    <v-tooltip bottom>
      <template #activator="{ on }">
        <div class="d-flex align-center ml-auto mr-2" v-on="on">
          <v-icon left> mdi-account</v-icon>
          <span class="font-weight-medium text-body-1">
            {{ userName }}
            <span v-if="isAdmin" class="ml-1 font-weight-bold"> (Admin) </span>
            <span
              v-else-if="isReadOnly"
              class="ml-1 font-weight-bold grey--text"
            >
              (Read-Only)
            </span>
          </span>
        </div>
      </template>
      <div class="pa-2">
        <div><strong>Χρήστης:</strong> {{ userData.username }}</div>
        <div><strong>Ρόλος:</strong> {{ getRoleText() }}</div>
        <div><strong>Δικαιώματα:</strong> {{ getPermissionsText() }}</div>
      </div>
    </v-tooltip>

    <v-divider vertical class="mx-4" />

    <!-- Live Clock -->
    <span class="font-weight-medium text-body-2 mx-3">
      <v-icon left small>mdi-clock-outline</v-icon>
      {{ currentTime }}
    </span>

    <v-divider vertical class="mx-4" />

    <!-- Dark Mode Toggle -->
    <v-tooltip bottom>
      <template #activator="{ on }">
        <v-btn icon v-on="on" @click="toggleDarkMode">
          <v-icon>
            {{ $vuetify.theme.dark ? 'mdi-brightness-7' : 'mdi-brightness-4' }}
          </v-icon>
        </v-btn>
      </template>
      <span>{{ $vuetify.theme.dark ? 'Φωτεινό θέμα' : 'Σκούρο θέμα' }}</span>
    </v-tooltip>

    <v-divider vertical class="mx-4" />

    <v-btn text rounded class="text-capitalize" @click="onLogout">
      <v-icon left> mdi-logout</v-icon>
      Εξοδος
    </v-btn>
  </v-app-bar>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  data() {
    return {
      currentTime: '',
      timeInterval: null
    }
  },
  computed: {
    ...mapState({
      userData: (state) => state.user.data
    }),
    ...mapGetters('activeRequest', ['activeRequestId']),
    userName() {
      return `${this.userData.username}`
    },
    isAdmin() {
      return (
        this.userData.role === 2 ||
        (Array.isArray(this.userData.role) && this.userData.role.includes(2)) ||
        (Array.isArray(this.userData.role) &&
          this.userData.role.includes('ADMIN_ROLE'))
      )
    },
    isReadOnly() {
      return (
        this.userData.role === 3 ||
        (Array.isArray(this.userData.role) && this.userData.role.includes(3)) ||
        (Array.isArray(this.userData.role) &&
          this.userData.role.includes('SECRETARY_ROLE'))
      )
    }
  },
  mounted() {
    this.updateTime()
    this.timeInterval = setInterval(this.updateTime, 1000)
    // Load dark mode preference from localStorage
    const darkMode = localStorage.getItem('darkMode') === 'true'
    this.$vuetify.theme.dark = darkMode
  },
  beforeDestroy() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval)
    }
  },
  methods: {
    ...mapActions(['logout']),
    ...mapActions('activeRequest', ['clearActiveRequest']),
    onLogout() {
      this.logout()
      this.$router.push('/login')
    },
    updateTime() {
      const now = new Date()
      const hours = now.getHours().toString().padStart(2, '0')
      const minutes = now.getMinutes().toString().padStart(2, '0')
      const seconds = now.getSeconds().toString().padStart(2, '0')
      this.currentTime = `${hours}:${minutes}:${seconds}`
    },
    toggleDarkMode() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark
      localStorage.setItem('darkMode', this.$vuetify.theme.dark)
      // Add smooth transition animation
      document.body.style.transition = 'background-color 0.3s ease'
    },
    getRoleText() {
      if (this.isAdmin) return 'Διαχειριστής'
      if (this.isReadOnly) return 'Μόνο Ανάγνωση'
      return 'Χρήστης'
    },
    getPermissionsText() {
      if (this.isAdmin) return 'Πλήρη δικαιώματα διαχείρισης'
      if (this.isReadOnly) return 'Προβολή μόνο, χωρίς δυνατότητα επεξεργασίας'
      return 'Βασικά δικαιώματα χρήστη'
    }
  }
}
</script>

<style lang="scss" scoped>
/* Moon glow for light mode */
.moon-glow {
  animation: moon-shimmer 3s ease-in-out infinite;
}

@keyframes moon-shimmer {
  0%,
  100% {
    box-shadow:
      0 0 10px rgba(99, 102, 241, 0.3),
      0 0 20px rgba(99, 102, 241, 0.2),
      0 0 30px rgba(99, 102, 241, 0.1);
  }
  50% {
    box-shadow:
      0 0 15px rgba(99, 102, 241, 0.5),
      0 0 30px rgba(99, 102, 241, 0.3),
      0 0 45px rgba(99, 102, 241, 0.2);
  }
}

/* Sun glow for dark mode */
.sun-glow {
  animation: sun-shimmer 3s ease-in-out infinite;
}

@keyframes sun-shimmer {
  0%,
  100% {
    box-shadow:
      0 0 10px rgba(251, 191, 36, 0.4),
      0 0 20px rgba(251, 191, 36, 0.3),
      0 0 30px rgba(251, 191, 36, 0.2);
  }
  50% {
    box-shadow:
      0 0 20px rgba(251, 191, 36, 0.6),
      0 0 35px rgba(251, 191, 36, 0.4),
      0 0 50px rgba(251, 191, 36, 0.3);
  }
}
</style>
